import { Drawer as AntDrawer } from "antd";
import { AlertTriangle, X } from "lucide-react";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import { AntThemed } from "@/components/ui/ant-theme";
import { PHONE, useMediaQuery } from "@/hooks/use-media-query";
import { Button, IconButton } from "@/components/ui/button";
import { cn } from "@/utils/utils";
import { useTranslation } from "@/store/locale";

/**
 * Every overlay in the app is a native `<dialog>` — on a screen wide enough to
 * hold one.
 *
 * The platform gives the focus trap, the return of focus on close, Escape, and
 * inertness of the page behind — four things that are easy to get wrong and
 * unforgivable to get wrong. All this adds is the shape.
 *
 * On a phone the shape is the thing that is wrong. A centred sheet assumes a
 * pointer, room around it, and a hand nowhere near the glass: it opens with its
 * actions stranded in the middle of the screen, out of reach of the thumb that
 * is holding the device, and any field in it fights the on-screen keyboard for
 * the same band of pixels. The same content rising from the bottom edge puts
 * the title where the eye is and the buttons where the thumb is, which is why
 * every `Modal` here becomes a bottom sheet under `sm` — antd's Drawer, already
 * in the app for the calendar and the CSV grid, doing the sliding.
 */
/**
 * How many overlays are holding the page still, and how to let it go again.
 *
 * A native modal blocks scrolling but leaves the scrollbar where it is, so a
 * drawer pinned to `right: 0` opens ten pixels shy of the edge with a strip of
 * the page showing beside it. Taking the scrollbar away closes that gap; the
 * width it occupied goes back as padding, so nothing behind the overlay shifts
 * while it does. Counted, because overlays stack — the filter drawer opens a
 * Save dialog over itself — and the first one to close must not hand the page
 * back while the second is still up.
 *
 * The document is not the only scroller, and on a shell route it is not the one
 * anybody sees: the shell is exactly the viewport with the content column
 * scrolling inside it, and a list screen on a short window gives that column a
 * scrollbar on purpose — the way to reach a table the floor has squeezed. The
 * body alone was therefore the wrong thing to hold: it hid a bar that was never
 * there and left the real one down the side of the sheet, beside the table's
 * own — two scrollbars against a page that could not be scrolled by either.
 * Every scroller the shell marks is held, the document among them for the
 * routes that have no shell.
 */
let overlays = 0;
let release: (() => void) | null = null;

/** The document, and whatever the shell has marked as a page scroller. */
const pageScrollers = (): HTMLElement[] => [
  document.body,
  ...document.querySelectorAll<HTMLElement>("[data-page-scroller]"),
];

/** Take one scroller's bar away, give its width back as padding, and undo both. */
function hold(element: HTMLElement) {
  const gap =
    element === document.body
      ? window.innerWidth - document.documentElement.clientWidth
      : element.offsetWidth - element.clientWidth;
  const overflow = element.style.overflow;
  const padding = element.style.paddingRight;
  element.style.overflow = "hidden";
  if (gap > 0) element.style.paddingRight = `${gap}px`;
  return () => {
    element.style.overflow = overflow;
    element.style.paddingRight = padding;
  };
}

/**
 * `scope: "shell"` leaves the document alone.
 *
 * antd locks the body itself for a drawer, and to put it back it first records
 * the inline styles it finds there. Locking the body ourselves before it looks
 * means what it records is `overflow: hidden` — so the page it hands back on
 * close is a page that can no longer be scrolled. The bottom sheet therefore
 * holds only what antd does not know about: the column the shell scrolls.
 */
function holdPage(scope: "page" | "shell" = "page") {
  overlays += 1;
  if (overlays > 1) return;
  const scrollers =
    scope === "shell"
      ? pageScrollers().filter((element) => element !== document.body)
      : pageScrollers();
  const undo = scrollers.map(hold);
  release = () => undo.forEach((restore) => restore());
}

function releasePage() {
  overlays = Math.max(0, overlays - 1);
  if (overlays > 0 || !release) return;
  release();
  release = null;
}

function useModal(open: boolean, onClose: () => void, dismissible = true) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  useEffect(() => {
    if (!open) return;
    holdPage();
    return releasePage;
  }, [open]);
  // Escape closes a native `<dialog>` by itself, and hiding the X does not
  // stop it. A dialog that must not be dismissed has to refuse here too, or
  // `dismissible={false}` is a suggestion with a keyboard shortcut past it.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || dismissible) return;
    const refuse = (event: Event) => event.preventDefault();
    dialog.addEventListener("cancel", refuse);
    return () => dialog.removeEventListener("cancel", refuse);
  }, [dismissible]);
  // `close` fires for Escape and for the form's dialog method too, so the
  // caller's state can never drift out of step with what is on screen.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const handle = () => onClose();
    dialog.addEventListener("close", handle);
    return () => dialog.removeEventListener("close", handle);
  }, [onClose]);
  return ref;
}

/** Clicking the backdrop is a click on the dialog element itself, not its content. */
const dismissOnBackdrop =
  (onClose: () => void) => (event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target === event.currentTarget) onClose();
  };

/**
 * A dialog opened from inside a form is a sibling of it on screen and a child
 * of it in React's tree, which is the tree events travel up. A portal moves the
 * DOM node, not the propagation path: the dialog's own form submits, and React
 * carries that submit on to the form the + button was standing in, which saves
 * a half-filled record nobody asked for. The overlay ends the journey — its
 * contents' handlers have already run by the time it does.
 */
const keepSubmitInside = (event: React.FormEvent) => {
  event.stopPropagation();
};

const SIZE = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  /**
   * A table's worth of room: the width of the list screens, and wider when the
   * content needs it, within limits.
   *
   * For work whose size is not ours to pick — a grid of a file, two columns for
   * one update and twenty for another. Growing from nothing would make a
   * two-column file a narrow strip; the floor is what the list it came from
   * looks like. The ceiling is 90rem rather than the viewport: a file of twenty
   * columns on a wide monitor took the whole screen, and a dialog that edge to
   * edge stops reading as a dialog — the columns past the ceiling scroll
   * sideways in the grid, which is where that scroll belongs. The height is the
   * content's, so no empty half-page below it.
   */
  fit: "w-fit min-w-[min(80rem,calc(100vw-4rem))] max-w-[min(90rem,calc(100vw-4rem))] max-h-[calc(100dvh-4rem)]",
} as const;

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** One line under the title. Anything longer belongs in the body. */
  description?: ReactNode;
  /**
   * A glyph beside the title, in a ringed disc, for a dialog that interrupts
   * to do one thing — open the camera, confirm a deletion. It names the act
   * before the sentence is read; a dialog that merely asks a question does not
   * take one.
   */
  icon?: ReactNode;
  size?: keyof typeof SIZE;
  /** The actions row. Cancel on the left of the primary action, always. */
  footer?: ReactNode;
  /** A decision that has to be made: no X, no backdrop click, no Escape. */
  dismissible?: boolean;
  /**
   * The hairline between the body and the actions. On a long, scrolling body it
   * says where the content ends; on a two-line question it is a rule drawn
   * across nothing, so that kind of dialog turns it off.
   */
  footerDivider?: boolean;
  /**
   * Tighter padding and a shorter drop to the actions, for a sheet whose whole
   * content is a question and two buttons. The page-sized rhythm around three
   * lines of text is mostly air.
   */
  dense?: boolean;
  /**
   * The same rule under the title. Off by default — most dialogs ask one short
   * question, and a line under its heading is chrome around two sentences. A
   * dialog whose body is a list of its own rows turns it on, so the heading is
   * not read as the first of them.
   */
  titleDivider?: boolean;
  /**
   * Stay in the middle of the screen on a phone, rather than rising from the
   * bottom edge as every other sheet does.
   *
   * For a dialog that interrupts to ask something rather than to be worked in.
   * A bottom sheet is the shape of somewhere to go — it slides in from the edge
   * and a thumb can push it back out — and a question that has to be answered
   * before anything else happens is not somewhere to go. Held in the middle, it
   * reads as what it is: the page stopped, waiting.
   */
  centred?: boolean;
  className?: string;
  children?: ReactNode;
}

/**
 * Whether an overlay should rise from the bottom edge rather than sit in the
 * middle of the screen.
 *
 * The viewport, not an element: an overlay covers the screen, so the screen is
 * the thing worth measuring — one of the few places in this app where a media
 * query is righter than a `ResizeObserver` on the frame.
 */
const useBottomSheet = () => useMediaQuery(PHONE);

/** How far a thumb has to push a sheet down before letting go dismisses it. */
const SWIPE_AWAY = 88;

/**
 * The header both shapes share: the glyph, the title, the line under it, the X.
 */
function SheetHead({
  titleId,
  title,
  description,
  icon,
  dismissible,
  dense,
  titleDivider,
  onClose,
}: Pick<ModalProps, "title" | "description" | "icon" | "onClose"> & {
  titleId: string;
  dismissible: boolean;
  dense: boolean;
  titleDivider: boolean;
}) {
  return (
    <header
      className={cn(
        "flex items-start justify-between gap-4",
        dense ? "px-5 pt-4 pb-2" : "px-6 pt-5 pb-4",
        titleDivider && "hairline-b",
      )}
    >
      {icon ? (
        <span
          aria-hidden
          className={cn(
            "bg-accent-soft text-accent grid size-11 shrink-0 place-items-center rounded-full",
            // The ring is the disc again at half strength: it lifts the glyph
            // off the sheet without drawing a second edge around it.
            "ring-accent-soft/60 ring-8",
          )}
        >
          {icon}
        </span>
      ) : null}
      <div className={cn("min-w-0", icon && "pt-0.5")}>
        <h2 id={titleId} className="text-title-3 text-heading">
          {title}
        </h2>
        {description ? (
          <p className="text-fg-muted text-subhead mt-1 text-pretty">{description}</p>
        ) : null}
      </div>
      {dismissible ? (
        <IconButton label="Close" size="icon-sm" className="-mt-1 -mr-2" onClick={onClose}>
          <X aria-hidden />
        </IconButton>
      ) : (
        <span />
      )}
    </header>
  );
}

/**
 * A modal is for one short, focused task. A whole page inside a modal is a page
 * that should have stayed a page — use a Drawer for context, a route for work.
 *
 * Centred on a desktop, risen from the bottom edge on a phone. Same props, same
 * children, same everything a caller writes: which of the two is on screen is a
 * question about the screen, not about the dialog, so no call site is asked it.
 */
export function Modal(props: ModalProps) {
  const phone = useBottomSheet();
  return phone && !props.centred ? <BottomSheet {...props} /> : <CentredModal {...props} />;
}

function CentredModal({
  open,
  onClose,
  title,
  description,
  icon,
  size = "md",
  footer,
  dismissible = true,
  footerDivider = true,
  titleDivider = false,
  dense = false,
  className,
  children,
}: ModalProps) {
  const ref = useModal(open, onClose, dismissible);
  const titleId = useId();
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClick={dismissible ? dismissOnBackdrop(onClose) : undefined}
      onSubmit={keepSubmitInside}
      className={cn(
        "animate-sheet bg-surface-raised text-fg m-auto w-[calc(100%-2rem)] p-0",
        "rounded-sheet max-h-[min(85dvh,44rem)] overflow-hidden shadow-[var(--shadow-sheet)]",
        "grid grid-rows-[auto_minmax(0,1fr)_auto]",
        SIZE[size],
        className,
      )}
    >
      <SheetHead
        titleId={titleId}
        title={title}
        description={description}
        icon={icon}
        dismissible={dismissible}
        dense={dense}
        titleDivider={titleDivider}
        onClose={onClose}
      />

      <div className={cn("overflow-y-auto pb-1", dense ? "px-5" : "px-6")}>{children}</div>

      {footer ? (
        <footer
          className={cn(
            "flex items-center justify-end gap-2",
            dense ? "mt-1 px-5 pb-4" : "mt-4 px-6 py-4",
            footerDivider && "hairline-t",
          )}
        >
          {footer}
        </footer>
      ) : (
        <div className="h-5" />
      )}
    </dialog>
  );
}

/**
 * The same sheet, standing on the bottom edge of a phone.
 *
 * antd's Drawer does the sliding and the mask, and nothing else: no header of
 * its own, no padding, no ground. What is drawn — the corners, the surface, the
 * shadow — is the one element inside it, which is also the element a thumb
 * drags, so pushing the sheet down moves the sheet rather than a transparent
 * box in front of it.
 */
function BottomSheet({
  open,
  onClose,
  title,
  description,
  icon,
  footer,
  dismissible = true,
  footerDivider = true,
  titleDivider = false,
  dense = false,
  className,
  children,
}: ModalProps) {
  const titleId = useId();
  const sheet = useRef<HTMLDivElement>(null);
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const shown = useRef(false);

  useEffect(() => {
    if (!open) return;
    holdPage("shell");
    return releasePage;
  }, [open]);

  // Shown as the sheet opens and hidden once it has finished leaving, rather
  // than the moment `open` goes false — a host taken away mid-flight takes the
  // closing animation with it.
  useEffect(() => {
    if (!host || !open || shown.current) return;
    shown.current = true;
    host.showPopover();
  }, [host, open]);

  const hideHost = () => {
    if (!host || !shown.current) return;
    shown.current = false;
    host.hidePopover();
  };

  // Whatever a drag left behind, undone while nothing is on screen to see it.
  useEffect(() => {
    if (open) return;
    const element = sheet.current;
    if (!element) return;
    element.style.transition = "";
    element.style.transform = "";
  }, [open]);

  const from = useRef<number | null>(null);
  const drag = (to: number | null) => {
    const element = sheet.current;
    if (!element) return;
    element.style.transition =
      to === null ? "transform var(--duration-base) var(--ease-out-soft)" : "none";
    element.style.transform = to ? `translateY(${to}px)` : "";
  };

  const grip = dismissible
    ? {
        onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => {
          // The X is in here too, and a press on it is a press on a button.
          if (event.button !== 0 || (event.target as HTMLElement).closest("button")) return;
          from.current = event.clientY;
          event.currentTarget.setPointerCapture(event.pointerId);
        },
        onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => {
          if (from.current === null) return;
          // Downward only: the sheet is already against the bottom of the
          // screen, and dragging it up opens a gap with nothing to put in it.
          drag(Math.max(0, event.clientY - from.current));
        },
        onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => {
          if (from.current === null) return;
          const pushed = event.clientY - from.current;
          from.current = null;
          event.currentTarget.releasePointerCapture(event.pointerId);
          // Far enough is gone: the transform stays where the thumb left it and
          // antd carries the sheet the rest of the way down from there.
          if (pushed > SWIPE_AWAY) onClose();
          else drag(null);
        },
        onPointerCancel: () => {
          from.current = null;
          drag(null);
        },
      }
    : {};

  return (
    <>
      {/* The top layer, the same borrow the toasts and the pickers make.
          antd's drawer is an ordinary portal at `z-index: 1000`, which is under
          every native `<dialog>` on the page — so a sheet opened from inside a
          drawer (the filter panel's Save, a picker's +) would open behind the
          thing that opened it. A `popover` host is in the top layer with the
          dialogs, and the last one shown is the one on top. */}
      <div
        ref={setHost}
        popover="manual"
        className="fixed inset-0 m-0 size-full overflow-visible border-0 bg-transparent p-0"
      />
      {host ? (
        <AntThemed>
          <AntDrawer
            open={open}
            onClose={onClose}
            placement="bottom"
            // As tall as what is in it, up to the cap the sheet itself carries:
            // a two-line confirmation does not take half the screen.
            size="auto"
            closable={false}
            title={null}
            footer={null}
            mask={{ enabled: true, closable: dismissible }}
            keyboard={dismissible}
            getContainer={host}
            aria-labelledby={titleId}
            afterOpenChange={(showing) => {
              if (!showing) hideHost();
            }}
            styles={{
              mask: { background: "var(--scrim)" },
              section: { background: "transparent", boxShadow: "none" },
              wrapper: { boxShadow: "none" },
              body: { padding: 0, background: "transparent", overflow: "visible" },
            }}
          >
            <div
              ref={sheet}
              onSubmit={keepSubmitInside}
              className={cn(
                "bg-surface-raised text-fg rounded-t-sheet flex max-h-[88dvh] flex-col",
                "overflow-hidden shadow-[var(--shadow-sheet)]",
                className,
              )}
            >
              {/* The grab bar and the header are one target: a sheet is pulled
                  down by the top of it, not by a four-pixel pill. `touch-none`
                  because the browser's own answer to a downward drag is to
                  scroll the page, and it takes the pointer stream with it. */}
              <div {...grip} className={dismissible ? "touch-none" : undefined}>
                {dismissible ? (
                  <div className="flex justify-center pt-2.5 pb-1">
                    <span aria-hidden className="bg-line-strong h-1 w-9 rounded-full" />
                  </div>
                ) : null}
                <SheetHead
                  titleId={titleId}
                  title={title}
                  description={description}
                  icon={icon}
                  dismissible={dismissible}
                  dense={dense}
                  titleDivider={titleDivider}
                  onClose={onClose}
                />
              </div>

              <div
                className={cn("min-h-0 flex-1 overflow-y-auto pb-1", dense ? "px-5" : "px-6")}
              >
                {children}
              </div>

              {/* The home indicator sits over the bottom of the glass on every
                  phone that has one, so the last row of the sheet clears it. */}
              {footer ? (
                <footer
                  className={cn(
                    "flex items-center justify-end gap-2",
                    "pb-[calc(1rem+env(safe-area-inset-bottom))]",
                    dense ? "mt-1 px-5" : "mt-4 px-6 pt-4",
                    footerDivider && "hairline-t",
                  )}
                >
                  {footer}
                </footer>
              ) : (
                <div className="h-[calc(1.25rem+env(safe-area-inset-bottom))]" />
              )}
            </div>
          </AntDrawer>
        </AntThemed>
      ) : null}
    </>
  );
}

/**
 * A drawer keeps the page behind it visible, which is the whole point: it is
 * for looking at, or adjusting, something in the context of the list it came
 * from. Filters, a record preview, a quick edit.
 */
export function Drawer({
  open,
  onClose,
  title,
  description,
  footer,
  lead,
  width = "md",
  className,
  children,
}: Omit<ModalProps, "size" | "dismissible"> & {
  /**
   * `auto` is as wide as what is in it, between a floor and today's ceiling.
   *
   * The fixed steps suit a drawer of fields, where the control is what has to
   * fit and a form reads badly cramped. A drawer of short rows — a list of
   * column names beside their switches — has nothing to fill a step with, so
   * `md` gave it four hundred pixels of which the right half was empty. This
   * takes the width the content asks for instead, never past `md`, so nothing
   * can come out wider than it is now and a long label still truncates where
   * it always did.
   */
  width?: "sm" | "md" | "lg" | "auto";
  /**
   * An action at the far left of the header, opposite the close button — a
   * sheet's own verb, for which the list it came from has no room.
   *
   * It centres the title: with a control on either side, a name pushed against
   * one of them reads as a label for that control rather than as the name of
   * the sheet. A drawer with a lead says what it is in the middle, and takes
   * no description — that line belongs to a sheet whose title sits left.
   */
  lead?: ReactNode;
}) {
  const ref = useModal(open, onClose);
  const titleId = useId();
  const widths = {
    sm: "sm:max-w-sm",
    md: "sm:max-w-md",
    lg: "sm:max-w-xl",
    // The floor is the header's own business — title, description and the
    // close button — under which a drawer stops looking like one; the ceiling
    // is `md`, so `auto` can only ever take width away.
    auto: "sm:w-max sm:min-w-[20rem] sm:max-w-md",
  } as const;
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClick={dismissOnBackdrop(onClose)}
      onSubmit={keepSubmitInside}
      className={cn(
        "animate-drawer bg-surface-raised text-fg fixed inset-y-0 right-0 left-auto",
        "m-0 h-dvh max-h-none w-full rounded-none p-0 shadow-[var(--shadow-sheet)]",
        "grid grid-rows-[auto_minmax(0,1fr)_auto]",
        widths[width],
        className,
      )}
    >
      {lead ? (
        <header className="hairline-b relative flex items-center justify-between gap-2 px-4 py-3.5">
          {lead}
          {/* Centred on the drawer rather than on what is left between the two
              controls: they are different widths, so a title measured from
              them sits visibly off the middle. Truncated, because a saved
              filter's name is somebody's to choose. */}
          <h2
            id={titleId}
            className="text-title-3 text-heading pointer-events-none absolute left-1/2 max-w-[55%] -translate-x-1/2 truncate text-center"
          >
            {title}
          </h2>
          <IconButton label="Close" size="icon-sm" onClick={onClose}>
            <X aria-hidden />
          </IconButton>
        </header>
      ) : (
        <header className="hairline-b flex items-start justify-between gap-4 px-5 pt-5 pb-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-title-3 text-heading">
              {title}
            </h2>
            {description ? (
              <p className="text-fg-muted text-subhead mt-1">{description}</p>
            ) : null}
          </div>
          <IconButton label="Close" size="icon-sm" className="-mt-1 -mr-2" onClick={onClose}>
            <X aria-hidden />
          </IconButton>
        </header>
      )}

      {/* `relative`: Tailwind's `sr-only` is `position: absolute`, so without a
          positioned ancestor a hidden label resolves against the document and
          stretches it. The same rule as the list table's scroll box. */}
      <div className="relative overflow-y-auto px-5 py-5">{children}</div>

      {footer ? (
        <footer className="hairline-t flex items-center justify-end gap-2.5 px-5 py-4">
          {footer}
        </footer>
      ) : null}
    </dialog>
  );
}

/**
 * A confirmation, for a consequence that is real and hard to walk back.
 *
 * Harmless actions do not get one — a dialog nobody reads is a dialog that
 * trains people not to read dialogs. The title asks the question, the body says
 * what it costs, and the button says what it does rather than "OK".
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  tone = "danger",
  loading = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * `danger` for what cannot be walked back, `warning` for a change that can —
   * a value being written over — and `primary` for a plain yes/no.
   */
  tone?: "danger" | "primary" | "warning";
  loading?: boolean;
}) {
  const t = useTranslation();
  confirmLabel ??= t("common.delete");
  cancelLabel ??= t("common.cancel");
  // Both consequential tones wear their sign: something is about to be written
  // over, or taken away, and the badge says so before the sentence does. The
  // question then sits in the badge's column, so the two read as one block
  // rather than as a heading with a paragraph adrift under it. A plain yes/no
  // gets no badge — there is nothing to warn about.
  const marked = tone !== "primary";
  const badge =
    tone === "danger"
      ? "bg-danger-soft text-danger"
      : "bg-warning-soft text-[var(--color-warning-400)]";
  return (
    <Modal
      open={open}
      onClose={onClose}
      footerDivider={false}
      // A confirmation is a question, not a place. See `centred`.
      centred
      dense
      title={
        marked ? (
          <span className="flex items-center gap-3">
            <span
              className={cn(
                "flex size-8 shrink-0 items-center justify-center rounded-full",
                badge,
              )}
            >
              <AlertTriangle className="size-4.5" aria-hidden />
            </span>
            <span className="min-w-0">{title}</span>
          </span>
        ) : (
          title
        )
      }
      size="sm"
      // The question goes in the header's own line-under-the-title slot rather
      // than in the body: the body scrolls, and a paragraph pulled up against
      // the title from inside a scroller has its first line clipped by it.
      description={
        marked ? <span className="text-callout block pl-12">{message}</span> : undefined
      }
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === "danger" ? "danger" : tone === "warning" ? "warning" : "primary"}
            size="sm"
            // A shade lighter than the palette's destructive red: this is a
            // question, not the answer, and a dialog that shouts gets clicked
            // through rather than read.
            className={cn(tone === "danger" && "bg-[var(--color-danger-500)]")}
            onClick={onConfirm}
            loading={loading}
            loadingLabel={confirmLabel}
            autoFocus
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {marked ? null : <p className="text-fg-muted text-callout text-pretty">{message}</p>}
    </Modal>
  );
}
